import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { prefersReducedMotion } from "../lib/gsap";

export function useGsapReveal({
  y = 36,
  x = 0,
  duration = 0.85,
  delay = 0,
  stagger = 0,
  start = "top 88%",
  immediate = false,
  ease = "power2.out",
  once = true,
} = {}) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const targets =
        stagger > 0 && el.children.length > 0
          ? gsap.utils.toArray(el.children)
          : [el];

      if (prefersReducedMotion()) {
        gsap.set(targets, { opacity: 1, x: 0, y: 0, clearProps: "transform" });
        return;
      }

      gsap.set(targets, { opacity: 0, x, y });

      const play = () => {
        gsap.to(targets, {
          opacity: 1,
          x: 0,
          y: 0,
          duration,
          delay,
          stagger,
          ease,
          clearProps: "transform",
        });
      };

      if (immediate) {
        play();
        return;
      }

      ScrollTrigger.create({
        trigger: el,
        start,
        once,
        onEnter: play,
      });
    }, el);

    return () => ctx.revert();
  }, [y, x, duration, delay, stagger, start, immediate, ease, once]);

  return ref;
}
